import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid,
    Box,
    Typography,
    Pagination,
    PaginationItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Popover,
    TableSortLabel } from '@mui/material';
import { CustomAccordion, TableRowsSkeleton } from '../../../shared';
import { ActionsIcon, Edit, Trash } from '../../../../assets';
import { useSelector, useDispatch } from 'react-redux';
import { deleteQuery, getAllTriggers, getTriggerById } from '../../../../redux/thunks/triggerThunk';
import { actions } from '../../../../redux/reducers/queriesReducer';
import ConfirmationDelete from '../confirmationDelete';
import routeNames from '../../../../routes/routeNames';
import { getSortedData } from '../../../../utility';
import { CompanyUserBasedTabs } from '../../../sidebar/constants';

function QueriesTable(props) {
    const { } = props;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);
    const {
        allTriggers, selectAll, selected, isChanged, isTableLoading 
    } = useSelector(state => state.query);
    const { company: selectedCompany } = useSelector(state => state.company);
    const {
        userDetails: {
            is_admin, is_staff = 0, email, company: userCompany 
        } 
    } = useSelector((state) => state.user);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const createSortHandler = (property) => () => {
        handleRequestSort(property);
    };

    const sortedData = React.useMemo(() => getSortedData(orderBy, order, allTriggers), [order, orderBy, allTriggers]);  
    useEffect(()=> {
        const matchingTab = CompanyUserBasedTabs.find(tab => tab.company === selectedCompany.id && tab.user === email);
        if(is_staff && userCompany?.id !== selectedCompany?.id && !matchingTab){
            navigate(routeNames.DASHBOARD);
        } else if(selectedCompany.id){
            dispatch(getAllTriggers({ companyId: selectedCompany.id }));
        }
      
    }, [selectedCompany.id, isChanged]);
    
    const handleCheckboxChange = (id) => {
        dispatch(actions.handleSelected({ type: 'SET_SELECTED_ROWS', selectedId: id }));
    };

    const handleSelectAll = () => {
        dispatch(actions.handleSelected({ type: 'SELECT_ALL' }));
    };

    const handlePopoverOpen = (event, rowId) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
        setSelectedRowId(rowId);
    };

    const handlePopoverClose = (row) => {
        setAnchorEl(null);
        setSelectedRowId(null);
    };

    const handleEdit = (rowId) => {
        dispatch(actions.handleQueryModal({ title: 'Edit query' }));
        dispatch(getTriggerById({ companyId: selectedCompany.id, triggerId: rowId }));
        handlePopoverClose();
    };

    const handleQueryRowClick = (event, rowId) => {
        const isCheckboxColumn = event.target.closest('.table-row-checkbox');
        const isActionsColumn = event.target.closest('.table-row-actions');
        const isPopoverOpen = Boolean(anchorEl);
        if (!isCheckboxColumn && !isActionsColumn && !isPopoverOpen) {
            handleEdit(rowId);
            handlePopoverClose();
        }
    };

    const handleDelete = (rowId) => {
        dispatch(deleteQuery({ companyId: selectedCompany.id, data: { ids: [rowId] } })).then(result => {
            if(result.payload.status_code === 200){
                dispatch(actions.setSelectAllFalse());
                setOpenDelete(!openDelete);
            }
            
        });
    };
    const handleDeleteOpen = () =>{
        setOpenDelete(!openDelete);
    };
    const onclose = () =>{
        handlePopoverClose();
        setOpenDelete(!openDelete);
    };
    const open = Boolean(anchorEl);

    const renderNoData = () => (
        <TableRow>
            <TableCell colSpan={5}>
                <Box p={3} textAlign="center">
                    <Typography className="text-sm-regular gray-900">
                            No Query data to display.
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );

    const renderTable = () => allTriggers.length > 0 ? sortedData?.map((row, index) => (
        <>
            <TableRow className="icon" key={row.id} onClick={(event)=> handleQueryRowClick(event, row.id)}>
                <TableCell className='table-row-data no-border table-row-checkbox' >
                    <Checkbox
                        checked={selected.includes(row.id)}
                        onChange={() => handleCheckboxChange(row.id)}
                    />
                </TableCell>
                <TableCell className='table-row-data no-border' >
                    {row.id}
                </TableCell>
                <TableCell className='table-row-data no-border' >
                    {row.name}
                </TableCell>
                <TableCell className='table-row-data no-border'>
                    <Box className="flex-wrap-center-flex-start-gap-4px">
                        {row?.campaigns?.map((query, index) => (
                            <React.Fragment key={index}>
                                <Box className="br-20px padding-tb-2-rl-8 bg-primary-500">
                                    <Typography className='tool-tip-label-name'>
                                        {query.name}
                                    </Typography>
                                </Box>
                            </React.Fragment>
                        ))}
                    </Box>
                </TableCell>
                <TableCell className='table-row-data no-border cursor-pointer table-row-actions'>
                    <img height={20} src={ActionsIcon} width={20} onClick={(event) => handlePopoverOpen(event, row.id)} />
                    <Popover
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                        }}
                        disableScrollLock={true}
                        open={selectedRowId === row.id}
                        sx={{ '& .MuiPaper-root': { boxShadow: 'none' } }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        onClose={handlePopoverClose}
                    >
                        <Box>
                            <Box
                                className="action-button-box table-row-actions"
                                onClick={() => handleEdit(row.id)}
                            >
                                <Edit color='#667085' />
                                <Typography className="action-button-text">
                                            Edit
                                </Typography>
                            </Box>
                            <Box
                                className="action-button-box table-row-actions"
                                onClick={handleDeleteOpen}
                            >
                                <Trash color='#667085' />
                                <Typography className="action-button-text" >
                                            Delete
                                </Typography>
                            </Box>
                                   
                        </Box>
                    </Popover>
                </TableCell>
            </TableRow>
            <ConfirmationDelete open={openDelete} onClosed={onclose} onConfirms={() => handleDelete(row.id)}/>
        </>))
        : renderNoData();

    const queriesTableComponent = () => (
        <TableContainer className='no-box-shadow no-border' component={Paper}>
            <Table className='table-border-collapse no-border'>
                <TableHead className='queries-table-heading'>
                    <TableRow>
                        <TableCell className="queries-headers-title">
                            <Checkbox
                                checked={selectAll}
                                onChange={handleSelectAll}
                            />
                        </TableCell>
                        <TableCell className="queries-headers-title">
                            <TableSortLabel active={orderBy === 'id'}
                                direction={orderBy === 'id' ? order : 'asc'}
                                onClick={createSortHandler('id')}>
                                ID
                            </TableSortLabel>
                        </TableCell>
                        <TableCell className="queries-headers-title">
                            <TableSortLabel active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={createSortHandler('name')}>
                                Name
                            </TableSortLabel>
                        </TableCell>
                        <TableCell className="queries-headers-title">Queries</TableCell>
                        <TableCell className="queries-headers-title" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isTableLoading ? (
                        <TableRowsSkeleton
                            columns={5}
                            rows={10}
                        />
                    ) : (
                        renderTable()
                    )}
                </TableBody>

            </Table>
        </TableContainer>
    );

    const queries = () => (<Grid container className='d-flex flex-direction-row' spacing={'24px'}>
        <Grid item xs={12}>
            <Box>
                {queriesTableComponent()}
            </Box>
        </Grid>
    </Grid>);

    return (
        <Box className ="b1px-border-box-br8px">
            <CustomAccordion
                accordionContainerBackground="bg-primary-50"
                accordionTitleColor="primary-color"
                acoordianDetailsBackground='bg-gray-50'
                byDefaultOpen={true}
                component={queries()}
                title="See Full List Below"
            />
        </Box>
    );
};


export default QueriesTable;